import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateAddProductSLStore } from '@features/shopping-list/add-product/useAddProductSLStore.ts';
import { Button } from '@components/ui/Button.tsx';
import { IconWrapper } from '@components/icons/IconWrapper.tsx';
import SvgRefreshing from '@components/icons/Refreshing.tsx';
import SvgPlaylistAdd from '@components/icons/PlaylistAdd.tsx';
import { cn } from '@utils/cn.ts';
import { ClientShoppingListSummaryDTO } from '@api/generated/search-service';

interface ComponentProps {
  priceVariantId: number;
  productId: number;
}

const ProductAddToShoppingList: React.FC<ComponentProps> = ({ priceVariantId, productId }) => {
  const { t } = useTranslation('client');
  const [showShoppingLists, setShowShoppingLists] = useState(false);
  const { error, adding, loading, loadShoppingLists, allShoppingLists, productInLists, createShoppingList, addToShoppingList, removeFromShoppingList } =
    useCreateAddProductSLStore();
  const [newShoppingListName, setNewShoppingListName] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadShoppingLists(priceVariantId, productId);
  }, []);

  // Zavření popup při kliknutí mimo komponentu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowShoppingLists(false);
      }
    };

    if (showShoppingLists) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShoppingLists]);

  const onChangeNewList = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewShoppingListName(e.target.value);
  };

  const onSaveNewList = () => {
    createShoppingList(newShoppingListName);
    setNewShoppingListName('');
  };

  const toggleCheckbox = (list: ClientShoppingListSummaryDTO, checked: boolean) => {
    if (checked) {
      addToShoppingList(list.id, productId);
    } else {
      removeFromShoppingList(list.id, productId);
    }
  };

  return (
    <div className={'flex flex-col gap-3'}>
      <div className="relative w-full" ref={containerRef}>
        <a className="inline-flex items-center  gap-1  text-controls-link-light-primary-default" onClick={() => setShowShoppingLists(!showShoppingLists)}>
          <SvgPlaylistAdd className={cn({ 'animate-spin': loading && !showShoppingLists })} />
          <span className={'basic-body-m-bold'}>{t('shoppingList.title')}</span>
        </a>
        {showShoppingLists && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-10" onClick={e => e.stopPropagation()}>
            {error && <div className="text-red-600 mb-2">{t('shoppingList.loadError', { error })}</div>}
            <div className="flex flex-col gap-2 mb-4 relative h-32 overflow-y-auto">
              {loading && (
                <IconWrapper className={'w-6 h-6 absolute right-0 top-0'}>
                  <SvgRefreshing className={'animate-spin'} />
                </IconWrapper>
              )}

              {[...allShoppingLists]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((list, listIndex) => {
                  const isInList = productInLists.some(p => p.listId === list.id);
                  return (
                    <div className="flex items-center gap-2" key={listIndex}>
                      <input
                        id={list.id.toString()}
                        key={list.id}
                        className="w-4 h-4 text-blue-600"
                        type="checkbox"
                        checked={isInList}
                        onChange={v => toggleCheckbox(list, v.target.checked)}
                      />
                      <label htmlFor="list-Test" className="text-sm">
                        {list.name} ({t('shoppingList.itemsCount', { count: list.productCount })})
                      </label>
                    </div>
                  );
                })}
            </div>
            <div className="flex gap-2 h-10">
              <input
                placeholder={t('shoppingList.newListPlaceholder')}
                className="flex-1 border border-gray-300 rounded px-3 py-1 text-sm"
                type="text"
                value={newShoppingListName}
                onChange={onChangeNewList}
              />
              <Button
                label={adding ? t('shoppingList.saving') : t('shoppingList.add')}
                disabled={newShoppingListName.length < 1}
                onClick={onSaveNewList}
                size={'md'}
                variant="fill"
                type={'primary'}
              ></Button>
            </div>
          </div>
        )}
      </div>
      {productInLists.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {productInLists.map(list => (
            <div
              key={list.listId}
              className="inline-flex items-center gap-1 pl-3 pr-2 py-0.5 bg-[#f1f4f5] hover:bg-[#e4e8ea] rounded-full text-base font-medium text-[#464c4f] transition-colors cursor-pointer"
              onClick={() => removeFromShoppingList(list.listId, productId)}
            >
              <span>{list.listName}</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.8337 5.3415L14.6587 4.1665L10.0003 8.82484L5.34199 4.1665L4.16699 5.3415L8.82533 9.99984L4.16699 14.6582L5.34199 15.8332L10.0003 11.1748L14.6587 15.8332L15.8337 14.6582L11.1753 9.99984L15.8337 5.3415Z" fill="#464C4F"/>
              </svg>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductAddToShoppingList;
